import { makeApi } from "@zodios/core";
import { makeClientWithHooks } from "api/helpers";
import { makePaginatedResult, paginationParams } from "api/resources/resource";
import { formatDate } from "date-fns";
import { z } from "zod";

export const certificationRequestStatusSchema = z.enum(["PENDING", "APPROVED", "REJECTED"]);
const certificationRequestReasonRejectionSchema = z.enum([
  "CERTIFICATION_INVALID",
  "CERTIFICATION_EXPIRED",
  "CERTIFICATION_INCOMPLETE",
  "WRONG_PHOTO",
  "NAME_MISMATCH",
  "NO_EXP_DATE",
  "WRONG_STATE",
  "CUSTOM",
]);

export const certificationRequestSchema = z.object({
  id: z.string(),
  certification_id: z.number(),
  status: certificationRequestStatusSchema,
  gigster_id: z.number(),
  expiration_date: z.coerce.date().nullish(),
  attachment_id: z.string().nullish(),
  state_code: z.string().nullish(),
  attachment_resource_url: z.string().nullish(),
  does_not_expire: z.boolean(),
  rejection_reason: certificationRequestReasonRejectionSchema.nullish(),
  custom_rejection_reason: z.string().max(200).nullish(),
  market_name: z.string().nullish(),
  user: z.object({
    id: z.number(),
    email: z.string().nullish(),
    first_name: z.string(),
    last_name: z.string(),
    phone_number: z.string().nullish(),
    preferred_name_for_search: z.string().nullish(),
  }),
});

export const certificationRequestReasonRejectionOptions = [
  { label: "Certification Invalid", value: certificationRequestReasonRejectionSchema.enum.CERTIFICATION_INVALID },
  { label: "Certification Expired", value: certificationRequestReasonRejectionSchema.enum.CERTIFICATION_EXPIRED },
  { label: "Certification Incomplete", value: certificationRequestReasonRejectionSchema.enum.CERTIFICATION_INCOMPLETE },
  { label: "Wrong Photo", value: certificationRequestReasonRejectionSchema.enum.WRONG_PHOTO },
  { label: "Name Mismatch", value: certificationRequestReasonRejectionSchema.enum.NAME_MISMATCH },
  { label: "No Expiration Date", value: certificationRequestReasonRejectionSchema.enum.NO_EXP_DATE },
  { label: "Wrong State", value: certificationRequestReasonRejectionSchema.enum.WRONG_STATE },
  { label: "Custom", value: certificationRequestReasonRejectionSchema.enum.CUSTOM },
] as const;

export type CertificationRequestReasonRejection = z.infer<typeof certificationRequestReasonRejectionSchema>;

export type CertificationRequest = z.infer<typeof certificationRequestSchema>;

export const certificationRequestListParams = [
  { name: "ordering", type: "Query", schema: z.string().optional() },
  { name: "status", type: "Query", schema: certificationRequestStatusSchema.optional() },
  { name: "rejection_reason", type: "Query", schema: certificationRequestReasonRejectionSchema.optional() },
  { name: "certification", type: "Query", schema: z.number().optional() },
  { name: "gigster", type: "Query", schema: z.number().optional() },
  { name: "state_code", type: "Query", schema: z.string().toUpperCase().optional() },
  { name: "created_ts", type: "Query", schema: z.string().optional() },
  { name: "updated_ts", type: "Query", schema: z.string().optional() },
  { name: "expiration_date", type: "Query", schema: z.string().optional() },
  { name: "last_updated_by", type: "Query", schema: z.string().optional() },
  { name: "created_by__id", type: "Query", schema: z.number().optional() },
  { name: "created_by__email", type: "Query", schema: z.string().optional() },
  { name: "created_by__first_name", type: "Query", schema: z.string().optional() },
  { name: "created_by__last_name", type: "Query", schema: z.string().optional() },
  { name: "created_by__preferred_name_for_search", type: "Query", schema: z.string().optional() },
  { name: "gigster__market__name", type: "Query", schema: z.string().optional() },
  { name: "gigster__market__id", type: "Query", schema: z.number().optional() },
  ...paginationParams,
] as const;

const certificationRequestsApi = makeApi([
  {
    method: "get",
    path: "/admin/certification-requests/",
    response: makePaginatedResult(
      z.preprocess((input) => {
        const maybeCert = input as CertificationRequest;
        if (!certificationRequestReasonRejectionSchema.safeParse(maybeCert.rejection_reason).success) {
          return {
            ...maybeCert,
            rejection_reason: certificationRequestReasonRejectionSchema.enum.CUSTOM,
            custom_rejection_reason: `${maybeCert.custom_rejection_reason ?? ""} ${maybeCert.rejection_reason}`,
          };
        }
        return input;
      }, certificationRequestSchema),
    ),
    alias: "list",
    parameters: certificationRequestListParams,
  },
  {
    method: "patch",
    path: "/admin/certification-requests/:id/",
    response: certificationRequestSchema,
    alias: "update",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: certificationRequestSchema
          .pick({
            certification_id: true,
            status: true,
            state_code: true,
            expiration_date: true,
            does_not_expire: true,
            rejection_reason: true,
            custom_rejection_reason: true,
          })
          .transform((data) => ({
            ...data,
            expiration_date: data.expiration_date ? formatDate(data.expiration_date, "yyyy-MM-dd") : "2099-12-31",
          })),
      },
    ],
  },
]);

export type CertificationRequestQueryPayload = NonNullable<
  Parameters<typeof certificationRequestsHooks.getKeyByAlias<"list">>[1]
>["queries"];

export type CertificationRequestUpdatePayload = NonNullable<
  Parameters<typeof certificationRequestsHooks.getKeyByAlias<"update">>[1]
>["body"];

export const { hooks: certificationRequestsHooks, client } = makeClientWithHooks(
  "certificationRequests",
  certificationRequestsApi,
);
